import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Collapse,
  Menu,
  MenuItem,
  FormControlLabel,
  Switch,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip
} from "@mui/material";
import {
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon
} from "@mui/icons-material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTranslation } from "react-i18next";
import NewProgramStartModal from "@features/programs/modals/NewProgramStartModal";
import { getDashboardData } from "../services/dashboardApi";
import { getAccessToken } from "@/utils/common";
import { getColumnDefinitions, getContainers } from "../config";
import {
  INITIAL_VISIBLE_CONTAINERS,
  INITIAL_EXPANDED_CONTAINERS,
  INITIAL_DASHBOARD_DATA,
  LOCALE_MAP,
  DEFAULT_LOCALE
} from "../constants";

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  
  // State for settings menu
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const settingsOpen = Boolean(settingsAnchorEl);

  // The dashboard is where a therapist lands after login, so the main job of
  // the app starts here rather than three clicks deep in a patient's page.
  // On desktop the floating button already follows them everywhere, so this one
  // would only be a second copy of the same action; phones get it here instead.
  const isMobile = useMediaQuery("(max-width:900px)");
  const [showNewProgramModal, setShowNewProgramModal] = useState(false);
  
  // State for container visibility
  const [visibleContainers, setVisibleContainers] = useState(INITIAL_VISIBLE_CONTAINERS);
  
  // State for container expansion
  const [expandedContainers, setExpandedContainers] = useState(INITIAL_EXPANDED_CONTAINERS);

  // State for API data - single state for all dashboard data
  const [dashboardData, setDashboardData] = useState(INITIAL_DASHBOARD_DATA);
  const [isLoading, setIsLoading] = useState(true);

  // Load dashboard data using single API call
  const loadData = async () => {
    setIsLoading(true);
    try {
      const token = getAccessToken();
      const response = await getDashboardData(token);
      
      // Assuming the API returns data in this structure:
      // {
      //   activePrograms: [...],
      //   latestFeedback: [...],
      //   programRequests: [...],
      //   latestPatients: [...],
      //   latestOwnExercises: [...],
      //   latestCommunityExercises: [...]
      // }
      setDashboardData(response);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // Keep empty arrays on error
      setDashboardData(INITIAL_DASHBOARD_DATA);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Generic table renderer that can handle all table types
  const renderGenericTable = (data, loading, columns, rowKeyProperty = 'id') => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (!data || data.length === 0) {
      return (
        <Typography variant="body2" sx={{ color: '#7c878e', textAlign: 'center', py: 3 }}>
          {t('dashboard.general.noContent')}
        </Typography>
      );
    }

    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              {columns.map((column) => (
                <TableCell key={column.key} sx={{ fontWeight: 'bold' }}>{column.header}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item) => (
              <TableRow key={item[rowKeyProperty]}>
                {columns.map((column) => {
                  const value = item[column.dataKey];

                  // Colorful status chips for review dashboard
                  if (column.type === 'reviewStatus') {
                    const statusColorMap = {
                      pending: 'warning',
                      in_review: 'info',
                      approved: 'success',
                      rejected: 'error'
                    };
                    return (
                      <TableCell key={column.key}>
                        <Chip
                          label={t(`review.status.${value}`, value)}
                          color={statusColorMap[value] || 'default'}
                          size="small"
                        />
                      </TableCell>
                    );
                  }

                  if (column.type === 'paymentStatus') {
                    const paymentColorMap = {
                      unpaid: 'default',
                      pending: 'warning',
                      paid: 'success'
                    };
                    return (
                      <TableCell key={column.key}>
                        <Chip
                          label={t(`review.payment.${value}`, value)}
                          color={paymentColorMap[value] || 'default'}
                          size="small"
                        />
                      </TableCell>
                    );
                  }

                  return (
                    <TableCell key={column.key}>
                      {value}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };



  // Get current date formatted
  const getCurrentDate = () => {
    const today = new Date();
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    
    const currentLanguage = i18n.language || 'de';
    const locale = LOCALE_MAP[currentLanguage] || DEFAULT_LOCALE;
    
    return today.toLocaleDateString(locale, options);
  };

  const handleSettingsClick = (event) => {
    setSettingsAnchorEl(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchorEl(null);
  };

  const toggleContainerVisibility = (containerKey) => {
    setVisibleContainers(prev => ({
      ...prev,
      [containerKey]: !prev[containerKey]
    }));
  };

  const toggleContainerExpansion = (containerKey) => {
    setExpandedContainers(prev => ({
      ...prev,
      [containerKey]: !prev[containerKey]
    }));
  };

  // Get column definitions and container configurations
  const columnDefinitions = getColumnDefinitions(t);
  const containers = getContainers(t, renderGenericTable, dashboardData, isLoading, columnDefinitions);

  return (
    <Container maxWidth={false} sx={{ py: 3, backgroundColor: '#f5f5f5', minHeight: 'calc(100dvh - 46px - 8px)' }}>
      {/* Header Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 2, md: 3 }, 
          mb: 3, 
          backgroundColor: 'white',
          borderRadius: 2,
          border: '1px solid #e0e0e0'
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ gap: 1.5, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ 
              color: '#000', 
              fontWeight: 600,
              fontSize: { xs: '1.5rem', md: '1.75rem' },
              mb: {xs: 0.5, md: 0}
            }}>
              {t('dashboard.header.today')} <span style={{ fontWeight: '400' }}>{getCurrentDate()}</span>
            </Typography>
            <Typography variant="subtitle1" sx={{ 
              color: '#7c878e',
              fontSize: '0.9rem'
            }}>
              {t('dashboard.header.subtitle')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {isMobile && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowNewProgramModal(true)}
                sx={{
                  backgroundColor: 'var(--main-color)',
                  color: 'white',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 1.75,
                  height: 40,
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    backgroundColor: 'color-mix(in srgb, var(--main-color), black 10%)'
                  }
                }}
              >
                {t('programs.newProgramCta')}
              </Button>
            )}
            <IconButton
              onClick={handleSettingsClick}
              sx={{
                color: '#7c878e',
                backgroundColor: '#f5f5f5',
                '&:hover': {
                  backgroundColor: '#e0e0e0'
                },
                width: 48,
                height: 48
              }}
            >
              <SettingsIcon />
            </IconButton>
          </Box>
        </Box>
      </Paper>

      <NewProgramStartModal
        show={showNewProgramModal}
        onHide={() => setShowNewProgramModal(false)}
      />

      {/* Settings Menu */}
      <Menu
        anchorEl={settingsAnchorEl}
        open={settingsOpen}
        onClose={handleSettingsClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 2,
            border: '1px solid #e0e0e0',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            minWidth: 280
          }
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#000' }}>
            {t('dashboard.settings.visibility')}
          </Typography>
          <Typography variant="body2" sx={{ color: '#7c878e', mt: 0.5 }}>
            {t('dashboard.settings.subtitle')}
          </Typography>
        </Box>
        {containers.map((container) => (
          <MenuItem 
            key={container.key}
            sx={{ 
              px: 2, 
              py: 1.5,
              '&:hover': {
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={visibleContainers[container.key]}
                  onChange={() => toggleContainerVisibility(container.key)}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: 'var(--main-color)',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: 'var(--main-color)',
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: '#000', fontWeight: 500 }}>
                  {container.title}
                </Typography>
              }
              sx={{ 
                width: '100%',
                margin: 0,
                justifyContent: 'space-between',
                '& .MuiFormControlLabel-label': {
                  flex: 1
                }
              }}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {containers.map((container) => (
          visibleContainers[container.key] && (
            <Grid 
              item 
              xs={12} 
              md={6} 
              key={container.key}
            >
              <Card 
                elevation={0}
                sx={{
                  backgroundColor: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                  }
                }}
              >
                <CardHeader
                  avatar={
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: '50%',
                        backgroundColor: '#f5f5f5',
                        color: '#7c878e',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {container.icon}
                    </Box>
                  }
                  title={
                    <Typography variant="h6" component="h2" sx={{ 
                      color: '#000', 
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      mb: 0,
                      lineHeight: 1.3
                    }}>
                      {container.title}
                    </Typography>
                  }
                  subheader={
                    <Typography variant="body2" sx={{ 
                      color: '#7c878e', 
                      mt: 0.25,
                      lineHeight: 1.4
                    }}>
                      {container.subtitle}
                    </Typography>
                  }
                  action={
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {container.hasAddButton && (
                        <Button
                          variant="contained"
                          size="small"
                          sx={{
                            backgroundColor: 'var(--main-color)',
                            color: 'white',
                            borderRadius: 2,
                            textTransform: 'none',
                            minWidth: 32,
                            width: 32,
                            height: 32,
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:hover': {
                              backgroundColor: 'color-mix(in srgb, var(--main-color), black 10%)'
                            }
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </Button>
                      )}
                      <IconButton
                        onClick={() => toggleContainerExpansion(container.key)}
                        aria-expanded={expandedContainers[container.key]}
                        size="small"
                        sx={{
                          color: '#7c878e',
                          '&:hover': {
                            backgroundColor: '#f5f5f5'
                          }
                        }}
                      >
                        {expandedContainers[container.key] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                  }
                  sx={{
                    pb: 1
                  }}
                />
                <Collapse in={expandedContainers[container.key]} timeout="auto" unmountOnExit>
                  <CardContent sx={{ pt: 0 }}>
                    {container.renderContent()}
                  </CardContent>
                </Collapse>
              </Card>
            </Grid>
          )
        ))}
      </Grid>
    </Container>
  );
}
